class PriceRangeFilter extends HTMLElement {
  connectedCallback() {
    this.rangeMin = parseFloat(this.dataset.rangeMin) || 0;
    this.rangeMax = parseFloat(this.dataset.rangeMax) || 100;
    this.currency = this.dataset.currency || "";

    this.hiddenMin = this.querySelector("[data-min]");
    this.hiddenMax = this.querySelector("[data-max]");
    this.displayMin = this.querySelector("[data-min-display]");
    this.displayMax = this.querySelector("[data-max-display]");
    this.thumbMin = this.querySelector('[data-thumb="min"]');
    this.thumbMax = this.querySelector('[data-thumb="max"]');
    this.fill = this.querySelector("[data-fill]");
    this.track = this.querySelector("[data-track]");

    this.currentMin = parseFloat(this.hiddenMin.value) || this.rangeMin;
    this.currentMax = parseFloat(this.hiddenMax.value) || this.rangeMax;

    this._onDrag = this._onDrag.bind(this);
    this._endDrag = this._endDrag.bind(this);

    this._updateUI();
    this._bindEvents();
  }

  _bindEvents() {
    this.displayMin.addEventListener("input", () => this._onDisplayInput("min"));
    this.displayMax.addEventListener("input", () => this._onDisplayInput("max"));
    this.displayMin.addEventListener("change", () => this._onDisplayChange("min"));
    this.displayMax.addEventListener("change", () => this._onDisplayChange("max"));

    this.thumbMin.addEventListener("mousedown", (e) => this._startDrag(e, "min"));
    this.thumbMax.addEventListener("mousedown", (e) => this._startDrag(e, "max"));
    this.thumbMin.addEventListener("touchstart", (e) => this._startDrag(e, "min"), { passive: true });
    this.thumbMax.addEventListener("touchstart", (e) => this._startDrag(e, "max"), { passive: true });

    this.thumbMin.addEventListener("keydown", (e) => this._onKeyDown(e, "min"));
    this.thumbMax.addEventListener("keydown", (e) => this._onKeyDown(e, "max"));
  }

  _startDrag(e, thumb) {
    this._dragging = thumb;
    this.thumbMin.toggleAttribute("data-active", thumb === "min");
    this.thumbMax.toggleAttribute("data-active", thumb === "max");
    document.addEventListener("mousemove", this._onDrag);
    document.addEventListener("mouseup", this._endDrag);
    document.addEventListener("touchmove", this._onDrag, { passive: false });
    document.addEventListener("touchend", this._endDrag);
  }

  _onDrag(e) {
    if (e.cancelable) e.preventDefault();
    const rect = this.track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const value = Math.round(this.rangeMin + fraction * (this.rangeMax - this.rangeMin));

    if (this._dragging === "min") {
      this.currentMin = Math.min(value, this.currentMax);
    } else {
      this.currentMax = Math.max(value, this.currentMin);
    }

    this._updateSlider();
    this._updateDisplayInputs();
  }

  _endDrag() {
    document.removeEventListener("mousemove", this._onDrag);
    document.removeEventListener("mouseup", this._endDrag);
    document.removeEventListener("touchmove", this._onDrag);
    document.removeEventListener("touchend", this._endDrag);
    this.thumbMin.removeAttribute("data-active");
    this.thumbMax.removeAttribute("data-active");
    this._dragging = null;
    this._commitValues();
  }

  _onKeyDown(e, thumb) {
    const step = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1 }[e.key];

    if (e.key === "Home") {
      e.preventDefault();
      if (thumb === "min") this.currentMin = this.rangeMin;
      else this.currentMax = this.currentMin;
      this._updateUI();
      this._commitValues();
      return;
    }

    if (e.key === "End") {
      e.preventDefault();
      if (thumb === "min") this.currentMin = this.currentMax;
      else this.currentMax = this.rangeMax;
      this._updateUI();
      this._commitValues();
      return;
    }

    if (step === undefined) return;
    e.preventDefault();

    if (thumb === "min") {
      this.currentMin = Math.max(this.rangeMin, Math.min(this.currentMax, this.currentMin + step));
    } else {
      this.currentMax = Math.min(this.rangeMax, Math.max(this.currentMin, this.currentMax + step));
    }

    this._updateUI();
    this._commitValues();
  }

  _onDisplayInput(which) {
    const raw = parseFloat(which === "min" ? this.displayMin.value : this.displayMax.value);
    if (!isFinite(raw)) return;

    if (which === "min") {
      this.currentMin = Math.max(this.rangeMin, Math.min(this.currentMax, raw));
    } else {
      this.currentMax = Math.min(this.rangeMax, Math.max(this.currentMin, raw));
    }

    this._updateSlider();
    this._syncHiddenInputs();
    // input event from the display input already bubbles to the form
  }

  _onDisplayChange(which) {
    const raw = parseFloat(which === "min" ? this.displayMin.value : this.displayMax.value);

    if (which === "min") {
      this.currentMin = isFinite(raw) ? Math.max(this.rangeMin, Math.min(this.currentMax, raw)) : this.rangeMin;
    } else {
      this.currentMax = isFinite(raw) ? Math.min(this.rangeMax, Math.max(this.currentMin, raw)) : this.rangeMax;
    }

    this._updateUI();
    this._syncHiddenInputs();
  }

  _updateSlider() {
    const range = this.rangeMax - this.rangeMin;
    const minPct = ((this.currentMin - this.rangeMin) / range) * 100;
    const maxPct = ((this.currentMax - this.rangeMin) / range) * 100;

    this.fill.style.left = `${minPct}%`;
    this.fill.style.right = `${100 - maxPct}%`;
    this.thumbMin.style.left = `${minPct}%`;
    this.thumbMax.style.left = `${maxPct}%`;
    this.thumbMin.setAttribute("aria-valuenow", this.currentMin);
    this.thumbMin.setAttribute("aria-valuetext", `${this.currency}${this.currentMin}`);
    this.thumbMax.setAttribute("aria-valuenow", this.currentMax);
    this.thumbMax.setAttribute("aria-valuetext", `${this.currency}${this.currentMax}`);
  }

  _updateDisplayInputs() {
    this.displayMin.value = this.currentMin;
    this.displayMax.value = this.currentMax;
  }

  _updateUI() {
    this._updateSlider();
    this._updateDisplayInputs();
  }

  _syncHiddenInputs() {
    this._setHiddenValue(this.hiddenMin, this.currentMin, this.currentMin <= this.rangeMin);
    this._setHiddenValue(this.hiddenMax, this.currentMax, this.currentMax >= this.rangeMax);
  }

  _commitValues() {
    this._syncHiddenInputs();
    this.hiddenMin.dispatchEvent(new Event("input", { bubbles: true }));
  }

  _setHiddenValue(input, value, isAtBound) {
    if (isAtBound) {
      input.removeAttribute("name");
    } else {
      input.setAttribute("name", input.dataset.name);
    }
    input.value = value;
  }
}

if (!customElements.get("price-range-filter")) {
  customElements.define("price-range-filter", PriceRangeFilter);
}
